const BLOCKED_PARTS=new Set([
  'token','authorization','password','secret','payload','route','lat','lng','latitude','longitude',
  'coordinate','coordinates','photo','blob','email','cookie','apikey','api','key','prompt','journal',
  'note','notes','content','body','request','response','raw','media','image','location','address',
  'userid','user'
]);
const ALLOWED_META_KEYS=new Set([
  'platform','screen','feature','count','filename','line','direction','driftms','queuedepth',
  'provider','providercode','code','retryable','timeout','cancelled','status','sessionref',
  'action','outcome','errorcode','queuebefore','queueafter','processed','retries','retry',
  'conflicts','recoveredcount','notificationkind','attempt','kind','reason','offline',
  'httpstatus','durationms','syncstate','networkstate','version','build','runtime',
  'environment','component','module','event','source','operation','phase','details','items',
  'metrics','flags','nested'
]);
const SEVERITIES=new Set(['debug','info','warn','error']);
const OUTCOMES=new Set(['success','failure','partial','cancelled','offline','unknown']);
const NETWORK_STATES=new Set(['online','offline','unknown','constrained']);
const SYNC_STATES=new Set(['idle','pending','syncing','success','partial','failure','offline','unknown']);
const MAX_DEPTH=4;
const MAX_ARRAY=20;
const MAX_KEYS=30;

function boundedLimit(value,fallback=60){
  const number=Math.floor(Number(value));
  return Number.isFinite(number)&&number>0?Math.min(2000,number):fallback;
}

function boundedEntries(value,limit){
  return Array.isArray(value)?value.slice(-boundedLimit(limit)):[];
}

export function createMemoryDiagnosticBuffer({initial=[],limit=60}={}){
  const maxEntries=boundedLimit(limit);
  const entries=boundedEntries(initial,maxEntries);
  return Object.freeze({
    read:()=>entries.slice(),
    append(entry,overrideLimit=maxEntries){
      entries.push(entry);
      const bounded=boundedLimit(overrideLimit,maxEntries);
      if(entries.length>bounded)entries.splice(0,entries.length-bounded);
      return entry;
    },
    clear(){
      const count=entries.length;
      entries.splice(0,entries.length);
      return count;
    }
  });
}

export function createStorageDiagnosticBuffer({
  storage,
  key='dymistrz:diagnostics:v1',
  limit=60
}={}){
  const maxEntries=boundedLimit(limit);
  let entries=[];
  try{
    const raw=storage?.getItem?.(key);
    entries=raw?boundedEntries(JSON.parse(raw),maxEntries):[];
  }catch{
    entries=[];
  }

  function persist(){
    try{storage?.setItem?.(key,JSON.stringify(entries));}catch{}
  }

  return Object.freeze({
    read:()=>entries.slice(),
    append(entry,overrideLimit=maxEntries){
      entries.push(entry);
      const bounded=boundedLimit(overrideLimit,maxEntries);
      if(entries.length>bounded)entries.splice(0,entries.length-bounded);
      persist();
      return entry;
    },
    clear(){
      const count=entries.length;
      entries=[];
      try{
        if(storage?.removeItem)storage.removeItem(key);
        else persist();
      }catch{}
      return count;
    }
  });
}

function keyParts(key){
  return String(key||'').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

function isBlockedKey(key){
  const parts=keyParts(key);
  return parts.some(part=>BLOCKED_PARTS.has(part))||parts.some((part,index)=>part==='api'&&parts[index+1]==='key');
}

function isAllowedMetaKey(key){
  if(isBlockedKey(key))return false;
  const normalised=String(key||'').toLowerCase().replace(/[^a-z0-9]+/g,'');
  return ALLOWED_META_KEYS.has(normalised);
}

function isBinary(value){
  if(typeof ArrayBuffer!=='undefined'){
    if(value instanceof ArrayBuffer)return true;
    if(ArrayBuffer.isView?.(value))return true;
  }
  return false;
}

function boundedText(value,max,fallback=''){
  const text=safeText(value,max).trim();
  return text||fallback;
}

function stableCode(value,fallback='DIAGNOSTIC_EVENT'){
  const normalised=String(value||fallback)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_.:-]+/g,'_')
    .replace(/^_+|_+$/g,'')
    .slice(0,80);
  return normalised||fallback;
}

function safeOpaqueId(value,max=80){
  const text=String(value||'').trim();
  if(!text||text.includes('@')||/\s/.test(text))return null;
  const safe=text.replace(/[^A-Za-z0-9_.:-]/g,'').slice(0,max);
  return safe||null;
}

function safeCorrelationId(value,{flowOnly=false}={}){
  const safe=safeOpaqueId(value);
  if(!safe)return null;
  const pattern=flowOnly?/^flow_[A-Za-z0-9_.:-]{1,74}$/:/^(?:flow|live|corr)_[A-Za-z0-9_.:-]{1,74}$/;
  return pattern.test(safe)?safe:null;
}

function normaliseSeverity(value){
  const severity=String(value||'info').toLowerCase();
  return SEVERITIES.has(severity)?severity:'info';
}

function normaliseOutcome(value){
  const outcome=String(value||'unknown').toLowerCase();
  return OUTCOMES.has(outcome)?outcome:'unknown';
}

function normaliseNetworkState(value){
  if(typeof value==='boolean')return value?'online':'offline';
  if(value&&typeof value==='object'&&typeof value.online==='boolean')return value.online?'online':'offline';
  const state=String(value?.state??value??'unknown').toLowerCase();
  return NETWORK_STATES.has(state)?state:'unknown';
}

function normaliseSyncState(value){
  const state=String(value||'unknown').toLowerCase();
  return SYNC_STATES.has(state)?state:'unknown';
}

function safeDuration(value){
  const number=Number(value);
  if(!Number.isFinite(number)||number<0)return null;
  return Math.min(86_400_000,Math.round(number));
}

function sanitizeValue(value,{depth=0,seen=new WeakSet()}={}){
  if(value===null)return null;
  const type=typeof value;
  if(type==='string')return safeText(value,120);
  if(type==='number')return Number.isFinite(value)?value:null;
  if(type==='boolean')return value;
  if(type==='bigint')return safeText(String(value),80);
  if(type==='undefined'||type==='function'||type==='symbol')return undefined;
  if(value instanceof Date)return Number.isNaN(value.getTime())?undefined:value.toISOString();
  if(isBinary(value))return undefined;
  if(depth>=MAX_DEPTH)return '[truncated]';
  if(seen.has(value))return '[circular]';

  seen.add(value);
  try{
    if(Array.isArray(value)){
      const out=[];
      for(const item of value.slice(0,MAX_ARRAY)){
        const safe=sanitizeValue(item,{depth:depth+1,seen});
        if(safe!==undefined)out.push(safe);
      }
      if(value.length>MAX_ARRAY)out.push('[truncated]');
      return out;
    }

    const out={};
    let accepted=0;
    for(const [key,item] of Object.entries(value)){
      if(accepted>=MAX_KEYS)break;
      if(!isAllowedMetaKey(key))continue;
      const safe=sanitizeValue(item,{depth:depth+1,seen});
      if(safe===undefined)continue;
      out[key]=safe;
      accepted+=1;
    }
    return out;
  }finally{
    seen.delete(value);
  }
}

export function safeText(value,max=240){
  return String(value??'')
    .replace(/Bearer\s+[A-Za-z0-9._~-]+/gi,'Bearer [redacted]')
    .replace(/\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/g,'[jwt]')
    .replace(/\b(?:api[_-]?key|access[_-]?token|token|auth(?:orization)?|password|secret)=([^\s&]+)/gi,match=>match.slice(0,match.indexOf('=')+1)+'[redacted]')
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,'[email]')
    .replace(/(-?\d{1,2}\.\d{4,})\s*[,/]\s*(-?\d{1,3}\.\d{4,})/g,'[coordinates]')
    .replace(/[\u0000-\u001f\u007f]/g,' ')
    .slice(0,max);
}

export function safeMeta(meta={}){
  const safe=sanitizeValue(meta);
  return safe&&typeof safe==='object'&&!Array.isArray(safe)?safe:{};
}

export function createDiagnostics({
  appVersion='unknown',
  appBuild='unknown',
  platform='unknown',
  runtime='web',
  limit=60,
  sink,
  buffer,
  now=()=>new Date().toISOString(),
  createId=()=>globalThis.crypto?.randomUUID?.()||`${Date.now()}-${Math.random()}`,
  networkState=()=>typeof navigator==='undefined'?'unknown':(navigator.onLine?'online':'offline')
}={}){
  const maxEntries=boundedLimit(limit);
  const memory=createMemoryDiagnosticBuffer({limit:maxEntries});

  function notifySink(entry){
    try{
      const result=sink?.(entry);
      if(result&&typeof result.then==='function')Promise.resolve(result).catch(()=>{});
    }catch{}
  }

  function nextId(prefix='diag'){
    const raw=safeOpaqueId(createId(),64)||stableCode(now(),'ID').toLowerCase();
    return `${prefix}_${raw}`.slice(0,80);
  }

  function buildEntry(input={}){
    const at=boundedText(input.at??input.timestamp??now(),80,new Date(0).toISOString());
    const version=boundedText(input.appVersion??input.app_version??input.version??appVersion,40,'unknown');
    const build=boundedText(input.appBuild??input.app_build??appBuild,40,'unknown');
    const eventPlatform=boundedText(input.platform??platform,40,'unknown');
    const eventRuntime=boundedText(input.runtime??runtime,40,'web');
    const module=boundedText(input.module??input.scope??'client',60,'client');
    const component=boundedText(input.component??'',60,'');
    const eventCode=stableCode(input.eventCode??input.event_code??input.code??'DIAGNOSTIC_EVENT');
    const network=normaliseNetworkState(input.networkState??input.network_state??networkState());
    const duration=safeDuration(input.durationMs??input.duration_ms);
    const correlationId=safeCorrelationId(input.correlationId??input.correlation_id);
    const flowId=safeCorrelationId(input.flowId??input.flow_id,{flowOnly:true});
    const syncState=normaliseSyncState(input.syncState??input.sync_state);
    const message=boundedText(input.message??'',240,'');

    return Object.freeze({
      id:safeOpaqueId(input.id)||nextId('diag'),
      at,
      timestamp:at,
      version,
      app_version:version,
      app_build:build,
      platform:eventPlatform,
      runtime:eventRuntime,
      severity:normaliseSeverity(input.severity),
      module,
      component:component||null,
      event_code:eventCode,
      outcome:normaliseOutcome(input.outcome),
      duration_ms:duration,
      correlation_id:correlationId,
      flow_id:flowId,
      network_state:network,
      sync_state:syncState,
      scope:module,
      code:boundedText(input.code??'',40,''),
      message,
      online:network==='unknown'?null:network==='online',
      meta:Object.freeze(safeMeta(input.meta))
    });
  }

  try{
    const restoredEntries=boundedEntries(buffer?.read?.(),maxEntries).map(raw=>buildEntry(raw));
    for(const restored of restoredEntries)memory.append(restored,maxEntries);
    if(buffer&&restoredEntries.length){
      try{
        buffer.clear?.();
        for(const restored of restoredEntries)buffer.append?.(restored,maxEntries);
      }catch{}
    }
  }catch{}

  function store(entry){
    memory.append(entry,maxEntries);
    try{buffer?.append?.(entry,maxEntries);}catch{}
    notifySink(entry);
    return entry;
  }

  function record(event={}){
    try{return store(buildEntry(event));}
    catch{return null;}
  }

  function capture(scope,error,meta={}){
    try{
      const code=boundedText(error?.code??error?.status??'',40,'');
      const {
        correlationId=null,
        correlation_id=null,
        flowId=null,
        flow_id=null,
        ...technicalMeta
      }=meta&&typeof meta==='object'&&!Array.isArray(meta)?meta:{};
      return store(buildEntry({
        severity:'error',
        module:scope||'client',
        eventCode:code||`${scope||'client'}.error`,
        outcome:'failure',
        code,
        message:error?.message??error??'Unknown error',
        correlationId:correlationId??correlation_id,
        flowId:flowId??flow_id,
        meta:technicalMeta
      }));
    }catch{return null;}
  }

  function createFlowId(){
    return nextId('flow');
  }

  function read(){
    return memory.read();
  }

  function exportSafe(){
    return Object.freeze({
      schema_version:1,
      generated_at:boundedText(now(),80,new Date(0).toISOString()),
      entries:Object.freeze(memory.read().map(entry=>Object.freeze({...entry,meta:Object.freeze(safeMeta(entry.meta))})))
    });
  }

  function serializeSafe(){
    return JSON.stringify(exportSafe());
  }

  function clear(){
    const count=memory.clear();
    try{buffer?.clear?.();}catch{}
    return count;
  }

  return Object.freeze({
    capture,
    record,
    createFlowId,
    read,
    exportSafe,
    serializeSafe,
    clear
  });
}
